import { DialogContent } from '@material-ui/core';

import { IconUpload } from 'src/assets/icons/Upload';
import { CustomDialog } from 'src/components/CustomDialog';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import TextLink from 'src/components/TextLink';
import { CHANNEL, getChannelPerformanceLabel, PLATFORM_CHANNEL } from 'src/data/channel';
import { EMAIL_CSV_RESOURCES, getResourceDocumentLabel } from 'src/data/performance';
import { useCustomerEloquaIntegrationEnabled } from 'src/store';

import { ChannelButton } from './components/ChannelButton';
import { useCsvTemplateUrl, useVisibleIntegrationChannels } from './services';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';

const EMAIL_INTEGRATIONS = [
  CHANNEL.hubspot,
  CHANNEL.salesforce,
  CHANNEL.marketo,
  CHANNEL.braze,
  CHANNEL.salesforceExactTarget,
];

const getEmailIntegrations = (customerSupportsEloqua) => {
  const integrations = [...EMAIL_INTEGRATIONS];
  if (customerSupportsEloqua) {
    integrations.push(CHANNEL.eloqua);
  }
  return integrations;
};

export const ConnectToChannelPopup = ({
  open,
  brandVoiceId,
  onSubmit,
  onClose,
  onConnectSuccess,
  displayIntegrations,
  displayResourcesTemplates,
  displayEmailIntegrations,
  displayBlogURLs,
  displayTalkingPointsFromResource,
  hideWtmIntegration,
  title,
  subtitle,
  isTrainingModel,
  isTalkingPointsView,
}) => {
  const styles = useStyles();

  const csvTemplateUrl = useCsvTemplateUrl();
  const integrationChannels = useVisibleIntegrationChannels({
    displayBlogURLs,
    hideWtmIntegration,
    isTrainingModel,
  });

  const { handleChannelClick, handleBlogPostsArticlesClick, handleResourceClick } = useHandlers({
    brandVoiceId,
    isTrainingModel,
    isTalkingPointsView,
    onSubmit,
    onClose,
    onConnectSuccess,
  });

  const customerSupportsEloqua = useCustomerEloquaIntegrationEnabled();

  const localEmailIntegrations = getEmailIntegrations(customerSupportsEloqua);

  return (
    <CustomDialog
      className={styles.root}
      open={open}
      onClose={onClose}
      title={title || 'Select Source'}
      data-testid="ConnectToChannelPopup"
      displayCloseIcon
      fullWidth
      hideActions
    >
      <DialogContent>
        <CustomTypography
          className={styles.description}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          {subtitle || 'Select an integration or resource'}
        </CustomTypography>

        <div className={styles.buttonsWrapper}>
          {displayIntegrations &&
            integrationChannels.map((channel) => (
              <ChannelButton
                className={styles.channelButton}
                key={channel}
                channel={channel}
                onClick={handleChannelClick}
              />
            ))}

          {displayTalkingPointsFromResource && (
            <>
              <ChannelButton
                className={styles.channelButton}
                channel={CHANNEL.other}
                onClick={handleChannelClick}
              />
              <ChannelButton
                className={styles.channelButton}
                channel={CHANNEL.generalDocument}
                label="File (PDF, TXT...)"
                onClick={handleChannelClick}
              />
            </>
          )}

          {displayBlogURLs && (
            <ChannelButton
              className={styles.channelButton}
              channel={PLATFORM_CHANNEL.blogUrls}
              onClick={handleBlogPostsArticlesClick}
            />
          )}

          {displayEmailIntegrations && (
            <>
              {localEmailIntegrations.map((channel) => (
                <ChannelButton
                  className={styles.channelButton}
                  key={channel}
                  channel={channel}
                  label={`${getChannelPerformanceLabel(channel)} emails`}
                  onClick={handleChannelClick}
                />
              ))}

              {!displayResourcesTemplates && (
                <ChannelButton
                  className={styles.channelButton}
                  channel={CHANNEL.emailChannel}
                  onClick={handleChannelClick}
                />
              )}
            </>
          )}

          {displayResourcesTemplates &&
            EMAIL_CSV_RESOURCES.map((csvResource) => (
              <ChannelButton
                className={styles.channelButton}
                key={csvResource}
                channel={csvResource}
                label={`${getResourceDocumentLabel(csvResource)} (CSV)`}
                onClick={handleResourceClick}
              />
            ))}
        </div>

        {displayResourcesTemplates && (
          <div className={styles.csvTemplateContainer}>
            <IconUpload className={styles.uploadIcon} />
            <span>
              Ensure your data import correctly by using{' '}
              <TextLink
                href={csvTemplateUrl}
                download="anyword_email_template"
                data-testid="downloadCsvTemplateLink"
              >
                our CSV template
              </TextLink>
            </span>
          </div>
        )}
      </DialogContent>
    </CustomDialog>
  );
};
